if (!customElements.get("quick-view-modal")) {
  customElements.define(
    "quick-view-modal",
    class QuickViewModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickViewInfo-"]');
        this.actionsContainer = this.querySelector('[id^="QuickViewActions-"]');
        if (!this.modalContent) return;
      }

      hide(preventFocus = false) {
        this.openedBy =
          this.openedBy
            ?.closest(".product-card")
            ?.querySelector(".product-card__name > a") || this.openedBy;
        if (preventFocus) this.openedBy = null;

        super.hide();

        this.addEventListener(
          "transitionend",
          () => {
            if (!this.hasAttribute("open")) {
              this.cacheRecipientFormValues();
              if (this.thumbsSwiper) {
                this.thumbsSwiper.destroy(true, true);
                this.thumbsSwiper = null;
              }
              if (this.mainSwiper) {
                this.mainSwiper.destroy(true, true);
                this.mainSwiper = null;
              }
              this.modalContent.innerHTML = "";
              if (this.actionsContainer) this.actionsContainer.innerHTML = "";
            }
          },
          { once: true }
        );

        document.body.dispatchEvent(new CustomEvent("quick-view-closed"));

        // styles for forced disable body scroll on iOS
        if (typeof window.unlockScrollBodyOnIos === "function") {
          window.unlockScrollBodyOnIos(this.openScrollY);
        }
      }

      show(opener) {
        if (!opener || !opener?.dataset?.productUrl) return;

        this.currentProductUrl = opener.dataset.productUrl;

        opener.setAttribute("aria-disabled", true);
        opener.classList.add("loading");
        const btnSpinner = opener.querySelector(".button-loader");
        if (btnSpinner) btnSpinner.classList.remove("hidden");

        fetch(opener.dataset.productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.productElement = responseHTML.querySelector(
              '[id^="MainProduct-"]'
            );
            if (!this.productElement) return;

            this.prepareProductHTML();
            this.setModalHTML();
            this.restoreRecipientFormValues();
            this.initSlider();
            this.initShopifyElements();
            super.show(opener);
            this.setFocusElement(opener);

            // styles for forced disable body scroll on iOS
            if (typeof window.lockScrollBodyOnIos === "function") {
              this.openScrollY = window.scrollY;
              window.lockScrollBodyOnIos(this.openScrollY);
            }
          })
          .finally(() => {
            opener.removeAttribute("aria-disabled");
            opener.classList.remove("loading");
            if (btnSpinner) btnSpinner.classList.add("hidden");
          });
      }

      prepareProductHTML() {
        this.preventDuplicatedIDs();
        this.removeDOMElements(this.productElement);
        this.expandDescription(this.productElement);
      }

      expandDescription(mainProductElement) {
        const description = mainProductElement.querySelector(
          ".product__description--collapsible"
        );
        if (!description) return;

        description.classList.remove("product__description--collapsible");

        const preview = description.querySelector(
          ".product__description__preview"
        );
        if (preview) preview.remove();

        const full = description.querySelector(".product__description__full");
        if (full) full.removeAttribute("hidden");

        const toggle = description.querySelector(
          ".product__description__toggle"
        );
        if (toggle) toggle.remove();
      }

      cacheRecipientFormValues() {
        const recipientForm = this.modalContent.querySelector("recipient-form");
        const checkbox = recipientForm?.querySelector('input[type="checkbox"]');

        if (!checkbox?.checked) {
          this.cachedRecipientForm = null;
          return;
        }

        this.cachedRecipientForm = {
          productUrl: this.currentProductUrl,
          email:
            recipientForm.querySelector('input[type="email"]')?.value || "",
          name:
            recipientForm.querySelector(
              'input[name="properties[Recipient name]"]'
            )?.value || "",
          message:
            recipientForm.querySelector('textarea[name="properties[Message]"]')
              ?.value || "",
          sendOn:
            recipientForm.querySelector('input[name="properties[Send on]"]')
              ?.value || "",
        };
      }

      restoreRecipientFormValues() {
        if (
          !this.cachedRecipientForm ||
          this.cachedRecipientForm.productUrl !== this.currentProductUrl
        ) {
          return;
        }

        const recipientForm = this.modalContent.querySelector("recipient-form");
        const checkbox = recipientForm?.querySelector('input[type="checkbox"]');
        if (!checkbox) return;

        checkbox.checked = true;
        checkbox.dispatchEvent(new Event("change", { bubbles: true }));

        const emailInput = recipientForm.querySelector('input[type="email"]');
        const nameInput = recipientForm.querySelector(
          'input[name="properties[Recipient name]"]'
        );
        const messageInput = recipientForm.querySelector(
          'textarea[name="properties[Message]"]'
        );
        const sendOnInput = recipientForm.querySelector(
          'input[name="properties[Send on]"]'
        );

        if (emailInput) emailInput.value = this.cachedRecipientForm.email;
        if (nameInput) nameInput.value = this.cachedRecipientForm.name;
        if (messageInput) messageInput.value = this.cachedRecipientForm.message;
        if (sendOnInput) sendOnInput.value = this.cachedRecipientForm.sendOn;
      }

      removeDOMElements(mainProductElement) {
        const selectors = [
          // Remove blocks from _product-blocks-under-gallery.liquid:
          ".product__gallery-blocks",
          // Remove blocks without specified data-attribute:
          ".product__info-container > :not([data-show-inside-quick-view='true'])",
          // Remove other blocks and elements:
          ".linked-products",
          ".product-popup",
          ".product-sticky-add-bar",
          ".pickup-availability",
          ".back-in-stock-alert",
          ".back-in-stock-alert-drawer",
          "[data-back-in-stock-alert-script]",
          ".product-form__checkout",
          "product-modal",
          ".product__media-toggle",
          ".product__media-mobile-icon",
          ".custom-liquid",
          ".product__price .product__tax",
          ".product__price .product__sku-inline",
          ".product__price-badges",
          ".block-heading__sale-badge",
        ];

        if (this.dataset.showQuantity === "false") {
          selectors.push(".product-form__quantity");
        }

        const removingElements = mainProductElement.querySelectorAll(
          selectors.join(", ")
        );
        removingElements.forEach((el) => el.remove());

        const onlyOneBlockSelectors = [
          ".product-parameters",
          ".product__vendor",
          ".product__price",
          ".product__title",
          ".product__inventory",
          ".product__buy-buttons",
        ];

        onlyOneBlockSelectors.forEach((selector) => {
          const items = mainProductElement.querySelectorAll(selector);
          if (items.length > 1) {
            items.forEach((el, index) => {
              if (index > 0) el.remove();
            });
          }
        });
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;

        this.productElement.innerHTML =
          this.productElement.innerHTML.replaceAll(
            sectionId,
            `quickview-${sectionId}`
          );

        this.productElement
          .querySelectorAll("variant-picker")
          .forEach((variantPicker) => {
            variantPicker.dataset.originalSection = sectionId;
          });
      }

      setModalHTML() {
        this.modalContent.innerHTML = this.productElement.innerHTML;

        this.reinjectScriptTags();
        this.moveBuyButtonsToActions();
        this.createFullDetailsButton();
        this.reorderElements();
        this.preventScrollBody();
        this.preventVariantURLSwitching();
        this.clearMediaClasses();
        this.usePrimarySubmitStyle();
      }

      reinjectScriptTags() {
        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        this.modalContent.querySelectorAll("script").forEach((oldScriptTag) => {
          const newScriptTag = document.createElement("script");
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(
            document.createTextNode(oldScriptTag.innerHTML)
          );
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      createFullDetailsButton() {
        const buttonsWrapper = this.querySelector(".product-form__buttons-row");
        const fullDetailsBtnTemplate = this.querySelector(
          "[data-full-details-button]"
        );
        if (!buttonsWrapper || !fullDetailsBtnTemplate) return;

        const fullDetailsBtn = fullDetailsBtnTemplate.content.cloneNode(true);

        buttonsWrapper.appendChild(fullDetailsBtn);
      }

      moveBuyButtonsToActions() {
        if (!this.actionsContainer) return;

        const buyButtons = this.modalContent.querySelector(
          ".product__buy-buttons"
        );
        if (!buyButtons) return;

        this.actionsContainer.appendChild(buyButtons);

        const recipientForm = buyButtons.querySelector("recipient-form");
        const infoContainer = this.modalContent.querySelector(
          ".product__info-container"
        );
        if (recipientForm && infoContainer) {
          infoContainer.appendChild(recipientForm);
        }
      }

      usePrimarySubmitStyle() {
        const submitBtn = this.querySelector(".product-form__submit");
        if (!submitBtn) return;

        submitBtn.classList.replace("button--secondary", "button--primary");
      }

      reorderElements() {
        this.groupTitleWithPrice();
      }

      groupTitleWithPrice() {
        const titleEl = this.modalContent.querySelector(".product__title");
        const priceEl = this.modalContent.querySelector(".product__price");
        if (!titleEl || !priceEl) return;

        const headingRow = document.createElement("div");
        headingRow.className = "product__heading-row";
        titleEl.before(headingRow);
        headingRow.append(titleEl, priceEl);
      }

      preventScrollBody() {
        this.modalContent
          .querySelectorAll(".product-form__controls-group input[type=radio]")
          .forEach((input) => {
            input.addEventListener("focus", (e) => {
              const scrollTop =
                document.body.scrollTop || document.documentElement.scrollTop;
              setTimeout(() => {
                window.scrollTo(0, scrollTop);
              }, 0);
            });
          });
      }

      preventVariantURLSwitching() {
        const variantPickerEl =
          this.modalContent.querySelectorAll("variant-picker");

        variantPickerEl.forEach((el) => {
          el.setAttribute("data-update-url", "false");
        });
      }

      clearMediaClasses() {
        // In this method, all classes are removed from product__media elements in order to initialize a forced media gallery type in the Quick view modal, independent of the product page.

        const mediaWrapper = this.modalContent.querySelector(".product__media");
        if (!mediaWrapper) return;

        const keepClasses = ["product__media", "product__media--no-media"];
        const filteredClasses = [...mediaWrapper.classList].filter((cssClass) =>
          keepClasses.includes(cssClass)
        );

        mediaWrapper.className = filteredClasses.join(" ");
      }

      initSlider() {
        if (this.thumbsSwiper) {
          this.thumbsSwiper.destroy(true, true);
          this.thumbsSwiper = null;
        }
        if (this.mainSwiper) {
          this.mainSwiper.destroy(true, true);
          this.mainSwiper = null;
        }

        const sliderEl = this.modalContent.querySelector(
          ".product__media-list"
        );
        if (!sliderEl) return;

        const isFeaturedImageOnly =
          this.dataset.mediaDisplay === "featured_image";

        if (isFeaturedImageOnly) {
          const thumbsWrapperEl = this.modalContent.querySelector(
            ".product__media-sublist-wrapper"
          );
          if (thumbsWrapperEl) thumbsWrapperEl.remove();

          // The single-image slider always behaves like a slider, no matter
          // which gallery type (grid/stacked/slider_previews/...) the main
          // product page is configured with: normalize the type attributes
          // so neither the CSS nor the galleryType check below treat it as
          // anything other than a plain slider.
          const mediaWrapperEl = this.modalContent.querySelector(
            ".product__media"
          );
          if (mediaWrapperEl) {
            mediaWrapperEl.dataset.desktopType = "slider";
            mediaWrapperEl.dataset.mobileType = "slider";
          }
          sliderEl.dataset.desktopType = "slider";
          sliderEl.dataset.mobileType = "slider";
          // updateMedia() in global.js looks up the slider by this class
          // (only present in markup for gallery types rendered as a slider)
          // to sync it to the selected variant after this initial setup.
          sliderEl.classList.add("js-media-list");
        }

        const sliderWrapper = sliderEl.querySelector(
          ".product__media-list-container"
        );
        const slides = sliderEl.querySelectorAll(".product__media-item");
        let buttonPrev = sliderEl.querySelector(".slider-nav-button--prev");
        let buttonNext = sliderEl.querySelector(".slider-nav-button--next");
        let paginationEl = sliderEl.querySelector(".slider-pagination");

        if (isFeaturedImageOnly) {
          if (paginationEl) {
            paginationEl.remove();
            paginationEl = null;
          }

          // Discard whatever nav markup the fetched product page happened to render
          // (it depends on the merchant's main gallery type, e.g. none for "slider_previews")
          // and rebuild it fresh so the single-image slider always gets hover arrows.
          sliderEl
            .querySelectorAll(".slider-navigation")
            .forEach((el) => el.remove());
          buttonPrev = null;
          buttonNext = null;

          if (slides.length > 1) {
            const navTemplate = this.querySelector(
              "[data-single-image-slider-nav]"
            );
            if (navTemplate) {
              sliderEl.appendChild(navTemplate.content.cloneNode(true));
              buttonPrev = sliderEl.querySelector(".slider-nav-button--prev");
              buttonNext = sliderEl.querySelector(".slider-nav-button--next");
            }
          }
        }

        if (!sliderWrapper || slides.length < 2) {
          if (buttonPrev) buttonPrev.remove();
          if (buttonNext) buttonNext.remove();
          if (paginationEl) paginationEl.remove();
          return;
        }

        const galleryType = sliderEl.dataset?.desktopType || "slider_previews";

        if (galleryType !== "slider" && galleryType !== "slider_previews") {
          return;
        }

        let thumbsSlider = null;

        const thumbsEl = this.modalContent.querySelector(
          ".product__media-sublist"
        );

        if (galleryType === "slider_previews" && thumbsEl) {
          thumbsSlider = this.thumbsSwiper = new Swiper(thumbsEl, {
            spaceBetween: 10,
            slidesPerView: "auto",
            loop: false,
            direction: "horizontal",
            allowTouchMove: true,
            watchSlidesProgress: true,
            watchOverflow: true,
            observer: true,
            observeParents: true,
            autoHeight: false,
            slideToClickedSlide: true,
            on: {
              touchEnd: function (swiper) {
                let range = 5;
                let diff = (swiper.touches.diff = swiper.isHorizontal()
                  ? swiper.touches.currentX - swiper.touches.startX
                  : swiper.touches.currentY - swiper.touches.startY);
                if (diff < range || diff > -range) swiper.allowClick = true;
              },
            },
            breakpoints: {
              576: {
                slidesPerView: "auto",
                direction: "vertical",
              },
            },
          });
        }

        sliderEl.classList.add("swiper");
        sliderWrapper.classList.add("swiper-wrapper");
        slides.forEach((slide) => slide.classList.add("swiper-slide"));

        const isLoop = true;

        this.mainSwiper = new Swiper(sliderEl, {
          slidesPerView: 1,
          spaceBetween: 10,
          autoHeight: false,
          loop: isLoop,
          direction: "horizontal",
          speed: 800,
          allowTouchMove: true,
          watchSlideProgress: true,
          mousewheel: {
            forceToAxis: true,
          },
          navigation: {
            nextEl: buttonNext,
            prevEl: buttonPrev,
            disabledClass: "swiper-button-disabled",
          },
          pagination: {
            el: paginationEl,
            clickable: true,
          },
          thumbs: {
            swiper: thumbsSlider ? thumbsSlider : "",
            multipleActiveThumbs: false,
            slideThumbActiveClass: "swiper-slide-thumb-active",
          },
          on: {
            slideChange: function () {
              window.pauseAllMedia(sliderEl);
              this.params.noSwiping = false;

              if (thumbsSlider) {
                const activeIndex = isLoop ? this.realIndex : this.activeIndex;
                thumbsSlider.slideTo(activeIndex);
              }
            },
            slideChangeTransitionEnd: function () {
              const activeIndex = isLoop ? this.realIndex : this.activeIndex;
              const model3D =
                this.slides[activeIndex]?.querySelector("model-viewer");
              const posterBtn3D = this.slides[activeIndex]?.querySelector(
                ".shopify-model-viewer-ui__button--poster"
              );
              if (model3D && posterBtn3D) {
                posterBtn3D.removeAttribute("hidden");
              }
            },
            touchStart: function () {
              const activeIndex = isLoop ? this.realIndex : this.activeIndex;
              const model3D =
                this.slides[activeIndex]?.querySelector("model-viewer");
              if (model3D) {
                if (
                  !model3D.classList.contains(
                    "shopify-model-viewer-ui__disabled"
                  )
                ) {
                  this.params.noSwiping = true;
                  this.params.noSwipingClass = "swiper-slide";
                } else {
                  this.params.noSwiping = false;
                }
              }
            },
          },
        });
      }

      initShopifyElements() {
        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        if (window.ProductModel) {
          window.ProductModel.loadShopifyXR();
        }
      }

      setFocusElement(opener) {
        const productCard = opener.closest("product-card");
        const productCardLink = productCard?.querySelector(
          ".product-card__link-overlay"
        );
        if (productCardLink) this.openedBy = productCardLink;
      }
    }
  );
}
